import _ from 'lodash';
import docCookies from 'cookies-js';
import { callPut } from './sagas';
import logging from './logging';
import * as normalizers from './normalizers';
import AuthenticationError from './AuthenticationError';
import BadRequestError from './BadRequestError';
import { token, authenticated } from '../entities/Session/selectors';
import * as SessionActions from '../entities/Session/actions';
import { request } from './Actions';
import getStore from '../store/getStore';
import InvalidRequestError from './InvalidRequestError';

export const SearchTypes = {
  CUSTOMERS: 'CUSTOMER',
  RESOURCES: 'RESOURCE',
  USERS: 'USERS',
  GEOCODES: 'GEOCODES',
  TAGS: 'TAGS'
};

export function queryString(obj) {
  return Object.keys(obj).map((key) => [key, obj[key]].map(encodeURIComponent).join('=')).join('&');
}

window.spoofUser = (username) => {
  if (username) {
    docCookies.set('spoofedUser', username, { expires: Infinity });
  } else {
    docCookies.expire('spoofedUser');
  }
};

export function ensureAbsoluteUrl(url) {
  if (url.startsWith('http')) {
    return url;
  }

  // eslint-disable-line
  return `${__API_BASE_URL__}${url}`;
}

function buildRequest(url, additionalOptions) {
  const headers = new Headers();
  headers.append('Content-Type', 'application/json');

  const state = getStore().getState();
  if (authenticated(state)) {
    headers.append('Authorization', `Bearer ${token(state)}`);
    if (docCookies.get('spoofedUser')) {
      headers.append('X-Switch-User', docCookies.get('spoofedUser'));
    }
  }

  const defaultOptions = {
    credentials: 'same-origin',
    headers,
  };

  const options = { ...defaultOptions, ...additionalOptions };
  return new Request(ensureAbsoluteUrl(url), options);
}

export function buildPaginatedUrl(
  url,
  {
    filters,
    page,
    sortColumns,
    sortOrders,
    limit,
    ...other
  } = {},
) {
  const sorting = sortColumns && sortColumns.length === 1
    ? { sort: sortColumns[0], direction: sortOrders[0] } : {};

  const queryArray = _.omitBy({
    page,
    ...normalizers.filtersToPayload(filters),
    ...sorting,
    ...{ limit },
    ...other,
  }, (v) => v === null || v === undefined || (Array.isArray(v) && v.length === 0));
  const qs = queryString(queryArray);

  return `${url}${qs === '' ? '' : `?${qs}`}`;
}

export const Endpoints = {
  admin: {
    createStaffingCompanyGroup: () => '/api/admin/staffing-company-groups',
    mainInfoStaffingCompanyGroup: (id) => `/api/admin/staffing-company-groups/${id}`,
  },
  absentReasons: () => '/api/absent-reasons',
  agreements: {
    get: ({ id }) => `/api/agreements/${id}`,
    // Slim v2 detail — agreement metadata + replacement-model summaries
    // (id, name, archived) only, no inline periods. Lazy-load each model's
    // periods via `pricelist.get` on accordion expand.
    getSlim: ({ id }) => `/api/v2/agreements/${id}`,
    patch: ({ id }) => `/api/agreements/${id}`,
    list: (options = {}) => buildPaginatedUrl('/api/agreements', options),
    create: () => '/api/agreements',
  },
  pricelist: {
    create: ({ agreementId }) => `/api/agreements/${agreementId}/replacement-models`,
    // Single replacement model with full period + deviation tree.
    get: ({ agreementId, replacementModelId }) => `/api/agreements/${agreementId}/replacement-models/${replacementModelId}`,
    patch: ({ agreementId, replacementModelId }) => `/api/agreements/${agreementId}/replacement-models/${replacementModelId}`,
    delete: ({ replacementModelId }) => `/api/replacement-models/${replacementModelId}`,
  },
  apiUsers: {
    create: () => '/api/api-users',
    get: ({ id }) => `/api/api-users/${id}`,
    list: (options = {}) => buildPaginatedUrl('/api/api-users', options),
    patch: ({ id }) => `/api/api-users/${id}`,
  },
  bookings: {
    cancelBookingPeriodActions: () => '/api/bookings/cancel-booking-period-actions',
    create: () => '/api/bookings',
    customerConfirmationEmail: ({ bookingId }) => `/api/bookings/${bookingId}/emails`,
    del: ({ id }) => `/api/bookings/${id}`,
    get: ({ id }) => `/api/v2/bookings/${id}`,
    list: (options = {}) => buildPaginatedUrl('/api/bookings', options),
    patch: ({ id }) => `/api/bookings/${id}`,
    patchResource: ({ id, resourceId }) => `/api/bookings/${id}/${resourceId}`,
    preview: () => '/api/bookings/preview',
    put: ({ id }) => `/api/bookings/${id}`,
    reportedPeriods: (options = {}) => buildPaginatedUrl('/api/reported-periods', options),
    reportedPeriodsSearches: (options = {}) => buildPaginatedUrl('/api/reported-period-searches', options),
    reportedPeriodsSearchesTimeReport: ({ id }) => `/api/reported-period-searches/${id}/time-report.pdf`,
    reportedPeriodsSearchesTimeReportCsv: ({ id }) => `/api/reported-period-searches/${id}/time-report.csv`,
    reportedPeriodsSearchesTimeReportXlsx: ({ id }) => `/api/reported-period-searches/${id}/time-report.xlsx`,
    reportedPeriodsSearchesDetailedTimeReportXlsx: ({ id }) => `/api/reported-period-searches/${id}/detailed-time-report.xlsx`,
    reportedPeriodsSearchesTimeReportPaxml: ({ id }) => `/api/reported-period-searches/${id}/time-report.pax`,
    reportedPeriodsSearchesSummaryPdf: ({ id }) => `/api/reported-period-searches/${id}/summary.pdf`,
    resourceConfirmationEmail: ({ bookingId, resourceId }) => `/api/bookings/${bookingId}/${resourceId}/emails`,
    resourceInvoiceItems: ({ bookingId, resourceId }) => `/api/bookings/${bookingId}/${resourceId}/invoice-items`,
    resourcePaymentItems: ({ bookingId, resourceId }) => `/api/bookings/${bookingId}/${resourceId}/payment-items`,
    unbookPeriods: ({ bookingId, ids }) => `/api/bookings/${bookingId}/periods/${ids.join(',')}`,
    weekly: (options = {}) => buildPaginatedUrl('/api/v2/bookings/weekly', options),
    emails: {
      customerPreview: ({ bookingId }) => `/api/bookings/${bookingId}/emails/preview`,
      resourcePreview: ({ bookingId, resourceId }) => `/api/bookings/${bookingId}/${resourceId}/emails/preview`,
    },
    smses: {
      resourcePreview: ({ bookingId, resourceId }) => `/api/bookings/${bookingId}/${resourceId}/smses/preview`,
    },
    downloadBookedPDF: ({
      bookingId, resourceId, from, to,
    }) => `/api/bookings/${bookingId}/${resourceId}/booked/${from}-${to}.pdf`,
    confirmationDocument: {
      preview: ({ bookingId, resourceId }) => `/api/bookings/${bookingId}/${resourceId}/confirmation-document/preview`,
      create: ({ bookingId, resourceId }) => `/api/bookings/${bookingId}/${resourceId}/confirmation-document`,
      status: ({ bookingId, resourceId }) => `/api/bookings/${bookingId}/${resourceId}/confirmation-document`,
      cancel: ({ bookingId, resourceId, documentId }) => `/api/bookings/${bookingId}/${resourceId}/confirmation-document/${documentId}`,
      send: ({ bookingId, resourceId, documentId }) => `/api/bookings/${bookingId}/${resourceId}/confirmation-document/${documentId}/send`,
    },
  },
  competencies: {
    list: (options = {}) => buildPaginatedUrl('/api/admin/competencies', options),
    get: ({ id }) => `/api/admin/competencies/${id}`,
    create: () => '/api/admin/competencies',
    patch: ({ id }) => `/api/admin/competencies/${id}`,
    delete: ({ id }) => `/api/admin/competencies/${id}`,
    importSources: () => '/api/admin/competencies/import-sources',
    importedBySource: ({ source }) => `/api/admin/competencies/imported/${source}`,
    createMapping: ({ id }) => `/api/admin/competencies/${id}/mappings`,
    deleteMapping: ({ id }) => `/api/admin/competency-mappings/${id}`,
    mappings: {
      list: ({ competencyId }) => `/api/admin/competencies/${competencyId}/mappings`,
      create: ({ competencyId }) => `/api/admin/competencies/${competencyId}/mappings`,
      delete: ({ id }) => `/api/admin/competency-mappings/${id}`,
    },
  },
  companyCompetencies: {
    list: () => '/api/company-competencies',
    get: ({ id }) => `/api/company-competencies/${id}`,
    create: () => '/api/company-competencies',
    patch: ({ id }) => `/api/company-competencies/${id}`,
    delete: ({ id }) => `/api/company-competencies/${id}`,
  },
  adminProfileItems: {
    list: () => '/api/admin/profile-items',
    get: ({ id }) => `/api/admin/profile-items/${id}`,
    create: () => '/api/admin/profile-items',
    patch: ({ id }) => `/api/admin/profile-items/${id}`,
    delete: ({ id }) => `/api/admin/profile-items/${id}`,
  },
  workArrangements: {
    list: () => '/api/staffing-company-group/work-arrangements',
    create: () => '/api/staffing-company-group/work-arrangements',
    patch: ({ id }) => `/api/staffing-company-group/work-arrangements/${id}`,
    delete: ({ id }) => `/api/staffing-company-group/work-arrangements/${id}`,
  },
  customers: {
    create: () => '/api/customers',
    get: ({ id } = {}) => (id ? `/api/customers/${id}` : '/api/customers'),
    exports: (options = {}) => buildPaginatedUrl('/api/generated-files/customer-data', options),
    list: (options = {}) => buildPaginatedUrl('/api/customers', options),
    views: ({ id }) => `/api/customers/${id}/views`,
    search: ({ ...options }) => buildPaginatedUrl('/api/customers/search', options),
    searches: ({ ...options }) => buildPaginatedUrl('/api/customer-searches', options),
    sendEmail: ({ ...options }) => buildPaginatedUrl('/api/customers/send-email', options),
    
  },
  days: () => '/api/days',
  documentTemplates: {
    list: (options = {}) => buildPaginatedUrl('/api/document-templates', options),
    get: ({ id }) => `/api/document-templates/${id}`,
    create: () => '/api/document-templates',
    update: ({ id }) => `/api/document-templates/${id}`,
    delete: ({ id }) => `/api/document-templates/${id}`,
    preview: ({ id }) => `/api/document-templates/${id}/preview`,
    placeholders: () => '/api/document-templates/placeholders',
  },
  employmentContracts: {
    create: () => '/api/employment-contracts',
    createPreview: () => '/api/employment-contract-previews',
    getPreview: ({ id }) => `/api/employment-contract-previews/${id}`,
    get: ({ id }) => `/api/employment-contracts/${id}`,
    list: (options = {}) => buildPaginatedUrl('/api/employment-contracts', options),
    pdfPreview: () => '/api/employment-contracts/pdf-preview',
    files: ({ id }) => `/api/employment-contracts/${id}/files`,
  },
  events: {
    list: (options = {}) => buildPaginatedUrl('/api/events', options),
    patch: (options = {}) => buildPaginatedUrl('/api/events', options),
    del: (options = {}) => buildPaginatedUrl('/api/events', options),
  },
  expenses: {
    booked: ({ bookingId, resourceId }) => `/api/bookings/${bookingId}/${resourceId}/booked-expenses`,
    reported: ({ bookingId, resourceId }) => `/api/bookings/${bookingId}/${resourceId}/reported-expenses`,
    expenseTypes: () => '/api/expense-types',
  },
  geoCodes: ({ ...options }) => buildPaginatedUrl('/api/geo-codes', options),
  areas: ({ ...options }) => buildPaginatedUrl('/api/areas', options),
  publicAreas: ({ ...options }) => buildPaginatedUrl('/public/resource-api/areas', options),
  consultantAreas: ({ ...options }) => buildPaginatedUrl('/public/resource-api/me', options),
  generatedFiles: {
    customerSchedules: (options = {}) => buildPaginatedUrl('/api/generated-files/customer-schedules', options),
  },
  holidays: (options = { limit: 1000 }) => buildPaginatedUrl('/api/holidays', options),
  invoices: {
    list: (options = {}) => buildPaginatedUrl('/api/invoices', options),
    create: (options = {}) => buildPaginatedUrl('/api/invoices', options),
    patch: ({ id }) => `/api/invoices/${id}`,
    pdf: ({ ids }) => `/api/invoices/${ids.join(',')}.pdf`,
    fakturaspecifikation: ({ id }) => `/api/invoices/${id}/fakturaspecifikation.pdf`,
    patchList: ({ ids }) => `/api/invoices/${ids.join(',')}`,
  },
  inquiries: {
    create: () => '/api/inquiries',
    get: ({ id }) => `/api/inquiries/${id}`,
    list: (options = {}) => buildPaginatedUrl('/api/inquiries', options),
    patch: () => '/api/inquiries',
    interestedPeople: ({ id }) => `/api/inquiries/${id}/apps`,
    acceptPerson: ({ id }) => `/api/inquiry-apps/${id}/accept`
  },
  jobBoards: {
    get: () => '/api/job-boards',
  },
  lists: {
    createList: (options = {}) => buildPaginatedUrl('/api/lists', options),
    get: ({ id }) => `/api/lists/${id}`,
    getResources: ({ listId }) => `/api/lists/${listId}/resources`,
    getCustomers: ({ listId }) => `/api/lists/${listId}/customers`,
    list: (options = {}) => buildPaginatedUrl('/api/lists', options),
    patch: ({ id }) => `/api/lists/${id}`,
    resources: {
      del: ({ listId, resourceId }) => `/api/lists/${listId}/resources/${resourceId}`,
    },
    customers: {
      del: ({ listId, customerId }) => `/api/lists/${listId}/customers/${customerId}`,
    },
  },
  municipalities: () => '/api/municipalities',
  newsletters: {
    create: (options = {}) => buildPaginatedUrl('/api/newsletters', options),
  },
  occupations: (options = {}) => buildPaginatedUrl('/api/occupations', options),
  oneflow: {
    collections: ({ staffingCompanyId }) => `/api/staffing-companies/${staffingCompanyId}/oneflow-collections`,
    ping: ({ staffingCompanyId }) => `/api/staffing-companies/${staffingCompanyId}/oneflow`,
    templates: ({ staffingCompanyId, oneflowCollectionId }) => `/api/staffing-companies/${staffingCompanyId}/oneflow-collections/${oneflowCollectionId}/oneflow-templates`,
  },
  payments: {
    create: () => '/api/payments',
    list: (options = {}) => buildPaginatedUrl('/api/payments', options),
    paymentCollection: ({ id }) => `/api/payment-collections/${id}`,
    paymentCollections: (options = {}) => buildPaginatedUrl('/api/payment-collections', options),
    pdf: ({ id }) => `/api/payments/${id}.pdf`,
    preview: (options = {}) => buildPaginatedUrl('/api/payments/preview', options),
    previewPDF: ({ resourceId, timeplanUnitsIds, ...options }) => buildPaginatedUrl(`/api/resources/${resourceId}/payments/preview-${timeplanUnitsIds}.pdf`, options),
    searches: () => '/api/payment-searches',
    searchesPdf: ({ id }) => `/api/payment-searches/${id}.pdf`,
    searchesPaxml: ({ id }) => `/api/payment-searches/${id}.pax`,
    hogiaxml: ({ id }) => `/api/payment-searches/${id}/hogia.xml`,
  },
  people: {
    create: () => '/api/persons',
    exports: (options = {}) => buildPaginatedUrl('/api/generated-files/person-data', options),
    events: ({ contactId }) => `/api/persons/${contactId}/events`,
    get: ({ contactId }) => `/api/persons/${contactId}`,
    list: (options = {}) => buildPaginatedUrl('/api/persons', options),
  },
  privacyPolicySmses: (options = {}) => buildPaginatedUrl('/api/privacy-policy-smses', options),
  public: {
    timeReporting: ({ uuid, secret }) => `/public/api/timereports/${uuid}/${secret}`,
    createTimeReporting: ({ uuid, secret, customerId }) => `/public/api/timereports/${uuid}/${secret}/${customerId}`,
    bankId: {
      auth: ({ staffingCompanyGroupId, ...options }) => buildPaginatedUrl(`/public/bankid/auth/${staffingCompanyGroupId}`, options),
      collect: ({ id }) => `/public/bankid/auth/collect/${id}`,
    },
    staffingCompanyGroups: ({ slug }) => `/public/staffing-company-groups/${slug}`,
    calendar: ({ resourceId, secret }) => `/public/calendar/${resourceId}/${secret}/ical.ics`,
    resourceApi: {
      absentReasons: () => '/public/resource-api/absent-reasons',
      attestedPeriods: ({ ...options }) => buildPaginatedUrl('/public/resource-api/attested-periods', options),
      availableDates: {
        list: ({ ...options }) => buildPaginatedUrl('/public/resource-api/available-dates', options),
        patch: () => '/public/resource-api/available-dates',
      },
      bookedPeriods: ({ ...options }) => buildPaginatedUrl('/public/resource-api/booked-periods', options),
      holidays: (options = { limit: 1000 }) => buildPaginatedUrl('/public/resource-api/holidays', options),
      me: () => '/public/resource-api/me',
      reportedPeriods: ({ ...options }) => buildPaginatedUrl('/public/resource-api/reported-periods', options),
      timeReports: () => '/public/resource-api/timereports',
      unreportedTimes: ({ ...options }) => buildPaginatedUrl('/public/resource-api/unreported-dates', options),
    },
    createLoginCode: () => '/public/codes',
    createLoginToken: () => '/public/tokens',
    requestResetPassword : () => '/public/reset-password/request',
    confirmPasswordReset: () => '/public/reset-password/reset'
  },
  recruitmentAds: {
    applications: {
      get: ({ id, ...options }) => buildPaginatedUrl(`/api/recruitment-ads/${id}/applications`, options),
    },
    create: () => '/api/recruitment-ads',
    get: ({ id }) => `/api/recruitment-ads/${id}`,
    list: (options = {}) => buildPaginatedUrl('/api/recruitment-ads', options),
  },
  v3VacancyInterests: {
    list: (options = {}) => buildPaginatedUrl('/api/v3-vacancy-interests', options),
    get: ({ id }) => `/api/v3-vacancy-interests/${id}`,
    dashboard: () => '/api/v3-vacancy-interests/dashboard',
  },
  recruitmentAdsSettings:{
    get: ( jobBoardType ) => `/api/recruitment-app-settings/${jobBoardType}`,
  },
  recruitmentStatuses: (options = {}) => buildPaginatedUrl('/api/recruitment-statuses', options),
  resources: {
    availableDates: {
      list: ({ resourceId, ...options }) => buildPaginatedUrl(`/api/resources/${resourceId}/available-dates`, options),
      patch: ({ resourceId }) => `/api/resources/${resourceId}/available-dates`,
    },
    basicSchemas: ({ resourceId }) => `/api/resources/${resourceId}/basic-schemas`,
    profileImages: ({ resourceId }) => `/api/resources/${resourceId}/profile-images`,
    profileItems: ({ resourceId }) => `/api/resources/${resourceId}/profile-items`,
    saveProfileItemByID: ({ resourceId, fieldId }) => `/api/resources/${resourceId}/profile-items/${fieldId}`,
    create: () => '/api/resources',
    expenses: {
      preview: ({ id }) => `/api/resources/${id}/expenses/preview`,
    },
    exports: (options = {}) => buildPaginatedUrl('/api/generated-files/resource-data', options),
    favourite: ({ userId, resourceId }) => `/api/users/${userId}/favourite-resources/${resourceId}`,
    get: ({ id }) => `/api/resources/${id}`,
    inquiries: ({ id, ...options }) => buildPaginatedUrl(`/api/resources/${id}/inquiries`, options),
    imports: {
      clean: () => '/api/cleaned-imports',
      post: () => '/api/imports',
    },
    list: (options = {}) => buildPaginatedUrl('/api/resources', options),
    patch: (options = {}) => buildPaginatedUrl('/api/resources', options),
    paxml: (options = {}) => buildPaginatedUrl('/api/resources.pax', options),
    qualifyingDeductions: {
      list: ({ resourceId, ...options }) => buildPaginatedUrl(
        `/api/resources/${resourceId}/qualifying-deductions`, options,
      ),
      post: ({ resourceId }) => `/api/resources/${resourceId}/qualifying-deductions`,
      preview: ({ resourceId, date }) => `/api/resources/${resourceId}/qualifying-deductions/${date}/preview`,
    },
    schedules: (options = {}) => buildPaginatedUrl('/api/resource-schedules', options),
    timeReportNotifications: ({ id }) => `/api/resources/${id}/time-report-notifications`,
    timeReports: ({ id, ...options }) => buildPaginatedUrl(`/api/resources/${id}/time-reports`, options),
    views: ({ id }) => `/api/resources/${id}/views`,
    replacementModels: ({ resourceId }) => `/api/resources/${resourceId}/replacement-models`,
    weeklyTimeReportSummary: ({ id, ...options }) => buildPaginatedUrl(`/api/resources/${id}/weekly-time-report-summaries`, options),
    search: ({ ...options }) => buildPaginatedUrl('/api/resources/search', options),
    searches: () => '/api/resource-searches',
    hogiaReport: ({ id }) => `/api/resource-searches/${id}/hogia.txt`,
    taxes: ({ id }) => `/api/resources/${id}/taxes`,
    smsConversation: ({ id, since }) => `/api/resources/${id}/smses${since ? `?since=${since}` : ''}`,
    resourcesPipeline: (options = {}) => buildPaginatedUrl('/api/resources-pipeline', options),
    resourcesPipelineUsers: (options = {}) => buildPaginatedUrl('/api/resources-pipeline/users', options),
    sendEmail: ({ ...options }) => buildPaginatedUrl('/api/resources/send-email', options),
    removeProfileItem: ({ fileId }) => `/api/resource-profile-items/${fileId}/remove-file`,
    pushNotifications: {
      send: ({ resourceId }) => `/api/resources/${resourceId}/push-notification`,
      history: ({ resourceId, ...options }) => buildPaginatedUrl(`/api/resources/${resourceId}/push-history`, options),
      preferences: ({ resourceId }) => `/api/resources/${resourceId}/push-notification-preferences`,
      requestDocumentUpload: ({ resourceId }) => `/api/resources/${resourceId}/request-document-upload`,
      portalVisibleProfileItems: ({ resourceId }) => `/api/resources/${resourceId}/portal-visible-profile-items`,
    },
  },
  emailSignature: {
    get: () => '/api/send-email',
  },
  replacementModels: {
    workTypes: ({
      replacementModelId,
      workTypeId,
      overtime,
      ...options
    }) => buildPaginatedUrl(
      `/api/replacement-models/${replacementModelId}/work-types/${workTypeId}${overtime ? `/${overtime}` : ''}`,
      options,
    ),
  },
  questionnaires: {
    get: () => buildPaginatedUrl('/api/questionnaires'),
  },
  services: {
    create: () => '/api/services',
    list: (options = { limit: 1000 }) => buildPaginatedUrl('/api/v2/services', options),
  },
  signableDocuments: {
    listByResource: ({ resourceId, ...options }) => buildPaginatedUrl(`/api/resources/${resourceId}/signable-documents`, options),
    create: ({ resourceId }) => `/api/resources/${resourceId}/signable-documents`,
    get: ({ id }) => `/api/signable-documents/${id}`,
    update: ({ id }) => `/api/signable-documents/${id}`,
    delete: ({ id }) => `/api/signable-documents/${id}`,
    send: ({ id }) => `/api/signable-documents/${id}/send`,
    cancel: ({ id }) => `/api/signable-documents/${id}/cancel`,
    timeline: ({ id }) => `/api/signable-documents/${id}/events`,
  },
  smses: {
    send: (options = {}) => buildPaginatedUrl('/api/smses', options),
    list: (options = {}) => buildPaginatedUrl('/api/incoming-smses', options),
  },
  staffingCompanies: {
    list: (options = { limit: 1000 }) => buildPaginatedUrl('/api/staffing-companies', options),
    patch: ({ id }) => `/api/staffing-company/${id}`,
    events: ({ id }) => `/api/staffing-company/${id}/events`,
    sequences: ({ id }) => `/api/staffing-companies/${id}/sequences`,
  },
  staffingCompanyGroup: () => '/api/staffing-company-group',
  staffingCompanyGroupSharedFiles: (options = {}) => buildPaginatedUrl('/api/shared-files', options),
  staffingCompanyGroupSharedFilesIsShared: ({ id }) => `/api/shared-files/${id}`,
  staffingCompanyGroupFiles: () => '/api/files',
  staffingCompanyGroupFilesDelete: ({ id }) => `/api/files/${id}`,
  staffingCompanyGroups: {
    get: ({ id }) => `/api/staffing-company-groups/${id}`,
    list: (options = {}) => buildPaginatedUrl('/api/staffing-company-groups', options),
    usage: ({ id, ...options }) => buildPaginatedUrl(`/api/staffing-company-groups/${id}/usage`, options),
    users: ({ id, ...options }) => buildPaginatedUrl(`/api/staffing-company-groups/${id}/users`, options),
    userRoles: ({ id, ...options }) => buildPaginatedUrl(`/api/users/${id}/roles`, options),
    patch: ({ id }) => `/api/staffing-company-groups/${id}`,
    competencies: {
      list: ({ id }) => `/api/admin/staffing-company-groups/${id}/competencies`,
      update: ({ id }) => `/api/admin/staffing-company-groups/${id}/competencies`,
    },
    v3VacancySyncConfig: {
      get: ({ id }) => `/api/admin/staffing-company-groups/${id}/v3-vacancy-sync-config`,
      save: ({ id }) => `/api/admin/staffing-company-groups/${id}/v3-vacancy-sync-config`,
      delete: ({ id }) => `/api/admin/staffing-company-groups/${id}/v3-vacancy-sync-config`,
      test: ({ id }) => `/api/admin/staffing-company-groups/${id}/v3-vacancy-sync-config/test`,
      createWebhook: ({ id }) => `/api/admin/staffing-company-groups/${id}/v3-vacancy-sync-config/webhook`,
      deleteWebhook: ({ id }) => `/api/admin/staffing-company-groups/${id}/v3-vacancy-sync-config/webhook`,
    },
    portalTenants: {
      list: ({ id }) => `/api/admin/staffing-company-groups/${id}/portal-tenants`,
      create: ({ id }) => `/api/admin/staffing-company-groups/${id}/portal-tenants`,
      update: ({ tenantId }) => `/api/admin/portal-tenants/${tenantId}`,
      delete: ({ tenantId }) => `/api/admin/portal-tenants/${tenantId}`,
    },
  },
  portalApiUsers: {
    list: () => '/api/admin/portal-api-users',
    create: () => '/api/admin/portal-api-users',
    patch: ({ id }) => `/api/admin/portal-api-users/${id}`,
  },
  statistics: {
    list: (options = {}) => buildPaginatedUrl('/api/statistics', options),
  },
  tags: {
    tags: () => "/api/tags",
    paginatedTags: (options = {}) => buildPaginatedUrl("/api/tags", options),
    delete: ({ entityType, entityId, tagId }) => `/api/${entityType}/${entityId}/tags/${tagId}`,
    addTags: ({ entityType, entityId }) => `/api/${entityType}/${entityId}/tags`,
  },
  tasks: {
    list: ({ userId, ...options }) => buildPaginatedUrl('/api/tasks', options),
  },
  terms: {
    create: () => '/api/terms',
    get: ({ id }) => `/api/terms/${id}`,
    list: (options = {}) => buildPaginatedUrl('/api/terms', options),
  },
  timeReports: {
    get: (options = {}) => buildPaginatedUrl('/api/time-reports', options),
    put: ({ bookingId, resourceId }) => `/api/bookings/${bookingId}/${resourceId}/time-reports`,
    download: ({
      bookingId, resourceId, year, week,
    }) => `/api/bookings/${bookingId}/${resourceId}/time-reports/${year}-${week}.pdf`,
    downloadPDF: ({
      bookingId, resourceId, from, to,
    }) => `/api/bookings/${bookingId}/${resourceId}/time-reports/${from}-${to}.pdf`,
    downloadPDFPage1: ({
      bookingId, resourceId, from, to,
    }) => `/api/bookings/${bookingId}/${resourceId}/time-reports/${from}-${to}-page1.pdf`,
    downloadResourcePDF: ({
      bookingId, resourceId, from, to,
    }) => `/api/bookings/${bookingId}/${resourceId}/resource-time-reports/${from}-${to}.pdf`,
  },
  tokens: () => '/api/tokens',
  twoFactor: () => '/api/2fa',
  texts: {
    create: (options = {}) => buildPaginatedUrl('/sms/new', options),
  },
  fortnox: {
    generateToken: '/api/fortnox-tokens',
    getTokens: ({ id }) => `/api/fortnox-tokens`,
    deleteToken: ({ id }) => `/api/fortnox-tokens/${id}`,
    saveArticleNumber: ({ id }) => `/api/fortnox-tokens/${id}`,
    adminTokenList: (options = {}) => buildPaginatedUrl("/api/admin/fortnox-tokens", options),
    adminDeleteToken: ({ id }) => `/api/admin/fortnox-tokens/${id}`,
  },
  user: () => '/api/user',
  users: {
    achievements: ({ id, ...options }) => buildPaginatedUrl(`/api/users/${id}/achievements`, options),
    budgets: ({ id, ...options }) => buildPaginatedUrl(`/api/users/${id}/budgets`, options),
    events: ({ id }) => `/api/users/${id}/events`,
    get: ({ id }) => `/api/users/${id}`,
    list: (options = {}) => buildPaginatedUrl('/api/users', options),
    statistics: ({ id, ...options }) => buildPaginatedUrl(
      `/api/users/${id}/statistics`,
      options,
    ),
    tasks: ({ id, ...options }) => buildPaginatedUrl(`/api/users/${id}/tasks`, options),
    views: ({ id }) => `/api/users/${id}/views`,
  },
  invoiceDeliveryTypes: () => '/api/invoice-delivery-types',
  version: () => '/api/version',
  profileItems: {
    allProfileItems: () => '/api/profile-items',
    scgProfileItems: () => '/api/staffing-company-group/profile-items',
    scgProfileItemById: ({id}) => `/api/staffing-company-group/profile-items/${id}`,
  },
  dropdownOptions: {
    salaryCodes: () => '/api/salary-codes'
  }
};

export const Mappings = {
  customer: ({ id }) => buildRequest(`/api/customers/${id}`),
  del: ({ url, payload }) => buildRequest(url, { method: 'DELETE', body: JSON.stringify(payload) }),
  get: ({ url }) => buildRequest(url),
  patch: ({ url, payload }) => buildRequest(url, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  }),
  post: ({ url, payload }) => buildRequest(url, {
    method: 'POST',
    body: JSON.stringify(payload),
  }),
  postFiles: ({ files, url = '/api/files' }) => {
    const formData = new FormData();
    files.map((file, index) => formData.append(`file_${index}`, file));
    const options = {
      method: 'POST',
      body: formData,
      credentials: 'same-origin',
      headers: new Headers(),
    };
    return buildRequest(url, options);
  },
  put: ({ url, payload }) => buildRequest(url, { method: 'PUT', body: JSON.stringify(payload) }),
  resource: ({ id }) => buildRequest(`/resources/${id}.json?latest_bookings=true&exclude_booking_id=`),
  customerViews: ({ id }) => buildRequest(Endpoints.customers.views({ id }), { method: 'POST' }),
  resourceViews: ({ id }) => buildRequest(Endpoints.resources.views({ id }), { method: 'POST' }),
};

function parseResponse(response) {
  const contentType = response.headers.get('Content-Type');
  if (!response.ok) {
    throw response;
  }

  if (response.status !== 204) {
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    }
    return response.blob();
  }

  return Promise.resolve();
}

function parseCatchedResponse(response) {
  const contentType = response && response.headers
    ? response.headers.get('Content-Type') : undefined;
  if (contentType && contentType.includes('application/json')) {
    if (!response) {
      throw new InvalidRequestError('Missig response data');
    }
    return response
      .json()
      .then((error) => {
        if (response.status === 401) {
          throw new AuthenticationError(error.errors);
        }

        if (error.errors) {
          throw new BadRequestError(error.errors);
        }

        throw new BadRequestError(error);
      });
  }

  if (response && typeof response.text === 'function') {
    return response
      .text()
      .then((error) => {
        throw new BadRequestError(error);
      });
  }

  throw new InvalidRequestError(response);
}

const shortLivedPromisedCache = {};
const fetchGet = (req) => {
  if (shortLivedPromisedCache[req.url]) {
    return shortLivedPromisedCache[req.url];
  }
  const fetchPromise = fetch(req)
    .then(parseResponse)
    .catch(parseCatchedResponse);
  shortLivedPromisedCache[req.url] = fetchPromise;
  return shortLivedPromisedCache[req.url].then((data) => {
    delete shortLivedPromisedCache[req.url];
    return data;
  }).catch((error) => {
    delete shortLivedPromisedCache[req.url];
    throw error;
  });
};
export function fetchRequest(req, successHandler, errorHandler) {
  const fetchPromise = req.method === 'GET'
    ? fetchGet(req)
    : fetch(req)
      .then(parseResponse)
      .catch(parseCatchedResponse);
  return fetchPromise
    .then(successHandler)
    .catch((error) => {
      if (error instanceof AuthenticationError) {
        getStore().dispatch(request(SessionActions.DEAUTH)());
      }

      if (!(error instanceof BadRequestError) && !(error instanceof AuthenticationError)) {
        logging.error(error);
      }

      if (!errorHandler) {
        console.warn('Warning! Error handler not provided.', error);
      } else {
        return errorHandler(error);
      }
      return error;
    });
}

export function apiCall(mapping, successHandler, errorHandler) {
  return callPut(fetchRequest, mapping, successHandler, errorHandler);
}

export function fetchSearch(searchString, type = 'default', searchParam = 'query') {
  const urls = {
    [SearchTypes.RESOURCES]: '/api/resources/search',
    [SearchTypes.CUSTOMERS]: '/api/customers/search',
    [SearchTypes.USERS]: '/api/users/search',
    [SearchTypes.GEOCODES]: '/api/geo-codes',
    [SearchTypes.TAGS]: '/api/tags',
  };

  const url = urls[type] || '/api/search';
  return fetch(buildRequest(`${url}?${searchParam}=${encodeURI(searchString)}`))
    .then(response => response.json())
    .then(data => data.items || []);
}

export const getResourceDetails = (resourceId, includeLatestBookings = false, excludeBookingId = '') => {
  const url = `/api/resources/${resourceId}.json?latest_bookings=${includeLatestBookings}&exclude_booking_id=${excludeBookingId}`;
  return fetch(buildRequest(url));
};

export const fetchApi = url => fetch(buildRequest(url));
